/**
 * Rechnen für den Banner-Zuschnitt: Welcher Ausschnitt des Originals landet
 * im Rahmen (620 × Höhe), und wie wird daraus das fertige Bild?
 *
 * Gespeichert wird der Mittelpunkt des Ausschnitts im Original (x, y von 0
 * bis 1), der Zoom (1 = Bild füllt den Rahmen) und die Höhe in der Mail —
 * unabhängig davon, wie groß der Rahmen gerade auf dem Bildschirm ist.
 */
import { BANNER_MAX_HOEHE, BANNER_MIN_HOEHE } from '../../lib/mailBuilder/render';

export const MAIL_BREITE = 620;
/** Das fertige Bild in doppelter Auflösung (scharf auf Retina-Displays). */
export const AUSGABE_BREITE = MAIL_BREITE * 2;
export const ZOOM_MAX = 4;

export interface Zuschnitt { x: number; y: number; zoom: number; hoehe: number }

export function hoeheBegrenzen(h: number): number {
  return Math.round(Math.min(BANNER_MAX_HOEHE, Math.max(BANNER_MIN_HOEHE, h || 280)));
}

/** Ausschnitt im Original (in Pixeln des Originals). */
export function ausschnitt(bildB: number, bildH: number, z: Zuschnitt): { sx: number; sy: number; sb: number; sh: number } {
  const verhaeltnis = hoeheBegrenzen(z.hoehe) / MAIL_BREITE;
  // Größter Ausschnitt mit dem Seitenverhältnis des Rahmens, der ins Bild passt.
  let sb = bildB;
  let sh = bildB * verhaeltnis;
  if (sh > bildH) { sh = bildH; sb = bildH / verhaeltnis; }
  const zoom = Math.min(ZOOM_MAX, Math.max(1, z.zoom || 1));
  sb /= zoom;
  sh /= zoom;
  const cx = Math.min(bildB - sb / 2, Math.max(sb / 2, (z.x ?? 0.5) * bildB));
  const cy = Math.min(bildH - sh / 2, Math.max(sh / 2, (z.y ?? 0.5) * bildH));
  return { sx: cx - sb / 2, sy: cy - sh / 2, sb, sh };
}

/** Mittelpunkt so begrenzen, dass der Rahmen immer ganz im Bild liegt. */
export function begrenzen(bildB: number, bildH: number, z: Zuschnitt): Zuschnitt {
  const a = ausschnitt(bildB, bildH, z);
  return { ...z, zoom: Math.min(ZOOM_MAX, Math.max(1, z.zoom || 1)), hoehe: hoeheBegrenzen(z.hoehe), x: (a.sx + a.sb / 2) / bildB, y: (a.sy + a.sh / 2) / bildH };
}

/** Bild laden (für die Leinwand mit CORS, damit sich das Ergebnis speichern lässt). */
export function bildLaden(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (!src.startsWith('blob:') && !src.startsWith('data:')) img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Das Bild ließ sich nicht laden.'));
    img.src = src;
  });
}

function alsDatei(canvas: HTMLCanvasElement, name: string, qualitaet: number): Promise<File> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => {
      if (!b) { reject(new Error('Das Bild ließ sich nicht umwandeln.')); return; }
      resolve(new File([b], name, { type: 'image/jpeg' }));
    }, 'image/jpeg', qualitaet);
  });
}

/** Original verkleinern (längste Seite höchstens 2400 px) und als JPEG vorbereiten. */
export async function originalVorbereiten(datei: File): Promise<File> {
  const url = URL.createObjectURL(datei);
  try {
    const img = await bildLaden(url);
    const faktor = Math.min(1, 2400 / Math.max(img.naturalWidth, img.naturalHeight));
    const c = document.createElement('canvas');
    c.width = Math.round(img.naturalWidth * faktor);
    c.height = Math.round(img.naturalHeight * faktor);
    const ctx = c.getContext('2d')!;
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.drawImage(img, 0, 0, c.width, c.height);
    return await alsDatei(c, datei.name.replace(/\.[^.]+$/, '') + '.jpg', 0.9);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Den gewählten Ausschnitt als fertigen Banner (1240 × doppelte Höhe) zeichnen. */
export async function bannerZeichnen(img: HTMLImageElement, z: Zuschnitt): Promise<File> {
  const hoehe = hoeheBegrenzen(z.hoehe);
  const c = document.createElement('canvas');
  c.width = AUSGABE_BREITE;
  c.height = hoehe * 2;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, c.width, c.height);
  ctx.imageSmoothingQuality = 'high';
  const a = ausschnitt(img.naturalWidth, img.naturalHeight, z);
  ctx.drawImage(img, a.sx, a.sy, a.sb, a.sh, 0, 0, c.width, c.height);
  return alsDatei(c, `banner-${Date.now()}.jpg`, 0.86);
}
